/**
 * Pure logic + types for the universal Check-In (redemption validation) engine.
 * Ported 1:1 from the Lovable source `_shared/checkin.ts` (plain JS, no TS).
 *
 * Every pass layout that needs a "redeem" / "claim" / "mark visited" moment
 * declares a CheckInSpec and CheckInSheet.vue handles the UI flow:
 *
 *   gps          → confirm you're inside a geofenced radius
 *   pin          → enter a numeric code given at the venue
 *   gps_or_pin   → either of the above (whichever the user can do)
 *   qr           → scan a QR code (full-screen camera surface)
 *   quiz         → answer a question correctly; supports attempts + cooldown
 *   gps_and_pin  → two-step: GPS first, then PIN
 *   gps_and_qr   → two-step: GPS first, then QR takeover
 *   gps_and_quiz → two-step: GPS first, then quiz
 *
 * CheckInSpec shape (per method):
 * {
 *   method, venueName, details?,
 *   radiusLabel?, simulatedProximity?,          // gps*
 *   pin?, pinLength?, hint?,                    // pin*
 *   instructions?,                              // qr*
 *   question?, choices?, correctIndex?,         // quiz*
 *   maxAttempts?, cooldownSec?, lockoutMin?,    // quiz*
 * }
 *
 * details = {
 *   description?, howToUse?[], restrictions?[], blackoutDates?[],
 *   isBlackoutToday?,
 *   frequency?: { cap, per: 'day'|'week'|'month'|'total', usedCount?, nextAvailableLabel? }
 * }
 */

export const CHECK_IN_METHODS = [
  'gps',
  'pin',
  'gps_or_pin',
  'qr',
  'quiz',
  'gps_and_pin',
  'gps_and_qr',
  'gps_and_quiz',
]

/**
 * Header metadata per method. `icon` is a lucide icon name — resolved to the
 * imported @lucide/vue component by the consuming SFC (keeps this module
 * pure logic).
 */
export const methodMeta = {
  gps: { label: 'Check in with GPS', icon: 'MapPin' },
  pin: { label: 'Check in with PIN', icon: 'Lock' },
  gps_or_pin: { label: 'Check in: GPS or PIN', icon: 'MapPin' },
  qr: { label: 'Scan QR to check in', icon: 'ScanLine' },
  quiz: { label: 'Answer to check in', icon: 'Brain' },
  gps_and_pin: { label: 'Check in: GPS + PIN', icon: 'MapPin' },
  gps_and_qr: { label: 'Check in: GPS + QR', icon: 'MapPin' },
  gps_and_quiz: { label: 'Check in: GPS + Quiz', icon: 'MapPin' },
}

/** Label of the secondary step in a gps_and_* combo. */
export const secondaryLabel = (method) =>
  method === 'gps_and_pin' ? 'PIN' : method === 'gps_and_qr' ? 'QR scan' : 'Quiz'

/**
 * Computed availability for a check-in, based on its details.
 * `ok = false` blocks the check-in with a reason shown in the UI.
 * Returns { ok, blockedLabel?, reason?, nextAvailableLabel? }.
 */
export const computeCheckInAvailability = (details) => {
  if (!details) return { ok: true }
  if (details.isBlackoutToday) {
    return {
      ok: false,
      blockedLabel: 'Blackout today',
      reason: "Today is a blackout date — check-in isn't available.",
      nextAvailableLabel: details.frequency?.nextAvailableLabel,
    }
  }
  if (details.frequency) {
    const { cap, usedCount = 0, per, nextAvailableLabel } = details.frequency
    if (usedCount >= cap) {
      const window =
        per === 'day' ? 'today' : per === 'week' ? 'this week' : per === 'month' ? 'this month' : 'in total'
      return {
        ok: false,
        blockedLabel: `Used ${usedCount}/${cap}`,
        reason: `You've reached ${cap} check-in${cap === 1 ? '' : 's'} ${window}.`,
        nextAvailableLabel,
      }
    }
  }
  return { ok: true }
}

/**
 * Computed availability for a pre-redemption offer, based on its terms.
 * `ok = false` blocks redemption with a reason shown in the UI.
 */
export const computeAvailability = (terms) => {
  if (!terms) return { ok: true }
  if (terms.isBlackoutToday) {
    return { ok: false, blockedLabel: 'Item Unavailable', reason: '' }
  }
  if (terms.frequency) {
    const { cap, usedCount = 0, per } = terms.frequency
    if (usedCount >= cap) {
      const window =
        per === 'day' ? 'today' : per === 'week' ? 'this week' : per === 'month' ? 'this month' : 'in total'
      return {
        ok: false,
        blockedLabel: `Used ${usedCount}/${cap}`,
        reason: `You've reached the limit of ${cap} ${cap === 1 ? 'use' : 'uses'} ${window}.`,
      }
    }
  }
  return { ok: true }
}

/** True when offer terms carry anything worth rendering. */
export const hasTerms = (terms) => {
  if (!terms) return false
  return Boolean(
    terms.description ||
    terms.restrictions?.length ||
    terms.howToUse?.length ||
    terms.blackoutDates?.length ||
    terms.frequency,
  )
}

/** True when check-in details carry anything worth rendering in the accordion. */
export const hasDetailsContent = (d) =>
  Boolean(d && (d.description || d.howToUse?.length || d.restrictions?.length || d.blackoutDates?.length || d.frequency))

/** Human label for a frequency `per` window ("per day" / "total"). */
export const perLabel = (per) =>
  per === 'day' ? 'per day' : per === 'week' ? 'per week' : per === 'month' ? 'per month' : 'total'
